import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useMutation as useConvexMutation } from "convex/react";
import { FavInboxContext } from "../context/favInbox.context";
import { refs } from "../convex/apiRefs";
import JobTile from "../screens/schedule/JobTile";
import { needsPersonText } from "../screens/jev/jevViewerModel";
import {
  jobKey,
  groupInbox,
  canReassign,
  isoToMdy,
  reassignDropId,
  menuPosition,
  fav2Line,
  inboxNeedsPerson,
  UNSCHEDULE_DROP_ID,
} from "../utility/favInbox";

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// FAV Inbox (#630) — the left pane's Inbox tab: FAV's bookings at this office that
// nobody has dealt with yet. Plan + decisions: docs/fav-inbox.md.
//
// Accordions (Leland 2026-10-13): Assigned, Unassigned, Deferred. Each booking's
// actions are in a ⋮ drop-down menu. Clicking the booking jumps the grid to its week,
// scrolls to the job and flashes it (FavInboxContext.requestJump).
//
// Actions (Leland 2026-10-13):
//   Unschedule — exactly a drag back to RTS (same drop id → same windows, same
//                FileMaker update). The job change is what clears the row.
//   Reassign   — only when FAV assigned a crew that's still on it: a drop on the
//                Unassigned row for the same date. Hidden until FAV books crews.
//   Approve    — the booking stands; row closed (Convex).
//   Defer      — seen but still unapproved: moves to Deferred, stays dimmed.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

const CHEVRON_DOWN = "M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z";
const CHEVRON_UP = "M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6 1.41 1.41z";

// The row's actions as a drop-down ("waterfall") menu — same look as the job tile's
// right-click menu. Closes on a pick, an outside click, Escape or a scroll.
// ⚠️ Colours are INLINE on purpose (copied from JobTile's menu): tailwind.config.js
// REPLACES the colour palette, so text-gray-* / bg-gray-* don't exist here and the
// items rendered white-on-white (UAT, v10-13-2026-03).
const MENU_STYLE = {
  backgroundColor: "white",
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
  padding: "4px 0",
  minWidth: "140px",
};
const ITEM_STYLE = {
  display: "block",
  width: "100%",
  padding: "8px 16px",
  textAlign: "left",
  backgroundColor: "transparent",
  border: "none",
  cursor: "pointer",
  fontSize: "14px",
  color: "#111827",
};
function ActionsMenu({ items, disabled }) {
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!pos) return undefined;
    const close = (e) => {
      if (e.type === "keydown" && e.key !== "Escape") return;
      if (e.type === "mousedown" && (menuRef.current?.contains(e.target) || btnRef.current?.contains(e.target))) return;
      setPos(null);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    window.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [pos]);
  // Once the menu has rendered (measurable), move it above the button / back inside
  // the window if it doesn't fit below — before the browser paints it.
  useLayoutEffect(() => {
    if (!pos || pos.placed || !menuRef.current || !btnRef.current) return;
    const m = menuRef.current.getBoundingClientRect();
    const next = menuPosition(
      btnRef.current.getBoundingClientRect(),
      { width: m.width, height: m.height },
      { width: window.innerWidth, height: window.innerHeight }
    );
    setPos({ ...next, placed: true });
  }, [pos]);
  const toggle = (e) => {
    e.stopPropagation();
    if (pos) return setPos(null);
    const r = btnRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 2, left: r.left, placed: false });
  };
  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-label="Actions"
        aria-haspopup="menu"
        aria-expanded={Boolean(pos)}
        disabled={disabled}
        className="px-1 text-base font-bold leading-none rounded-sm border border-transparent hover:border-[rgb(var(--color-fill-base))] disabled:opacity-50"
        onClick={toggle}
      >
        ⋮
      </button>
      {pos && (
        <div
          ref={menuRef}
          role="menu"
          style={{ ...MENU_STYLE, position: "fixed", zIndex: 9999, top: pos.top, left: pos.left }}
        >
          {items.map((it) => (
            <button
              key={it.label}
              type="button"
              role="menuitem"
              style={ITEM_STYLE}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f3f4f6")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
              onClick={(e) => {
                e.stopPropagation();
                setPos(null);
                it.onSelect();
              }}
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

function InboxRow({ row, project, onDrop, onJump }) {
  const approve = useConvexMutation(refs.favInboxApprove);
  const defer = useConvexMutation(refs.favInboxDefer);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showWhy, setShowWhy] = useState(false);
  const whyText = row.why && row.why.fav2 && row.why.fav2.why; // FAV 2.0's own explanation (#652)
  const isAdvice = row.status === "advice"; // TEMPORARY "Unscheduled" rows: nothing booked
  // FileMaker took the booking back to RTS (the customer declined): kept only so FAV 2.0's
  // answer can still be seen — off the grid, nothing to act on.
  const isCancelled = row.status === "customerCancelled";

  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn({ id: row.id });
    } catch (e) {
      console.warn("%cFavInbox action failed", "color: #f97316; font-weight: bold;", e);
      setError(String(e?.data || e?.message || e));
    } finally {
      setBusy(false);
    }
  };

  const personText = needsPersonText(inboxNeedsPerson(row.why)); // FAV 2.0's flag only (PRED-032)
  const advice = fav2Line(row.why); // FAV 2.0 advice (#642), when the Lambda sent one

  // Menu order = the order Leland listed them.
  const items = [];
  if (project) items.push({ label: "Unschedule", onSelect: () => onDrop(project, UNSCHEDULE_DROP_ID) });
  if (project && canReassign(row, project)) items.push({ label: "Reassign", onSelect: () => onDrop(project, reassignDropId(row.bookedDate)) });
  items.push({ label: "Approve", onSelect: () => run(approve) });
  if (row.status === "pending") items.push({ label: "Defer", onSelect: () => run(defer) });

  return (
    <div className="mb-2" data-testid="fav-inbox-row" data-job-id={row.jobId}>
      <div className="flex items-start gap-1">
        <div className="flex-1 min-w-0">
          {/* Clicking the booking jumps the grid to it (not the job window). */}
          {isCancelled ? (
            <div className="w-full text-left text-xs px-2 py-1 bg-skin-primary rounded-sm opacity-80" data-testid="fav-inbox-cancelled-label">
              {row.kellerId} · booked {isoToMdy(row.bookedDate)}
            </div>
          ) : project ? (
            <JobTile project={project} projectDetailClick={() => onJump(row)} draggable={false} favDim={false} className="bg-skin-primary" />
          ) : (
            <button type="button" className="w-full text-left text-xs px-2 py-1 bg-skin-primary rounded-sm" onClick={() => onJump(row)}>
              {row.kellerId} · {isoToMdy(row.bookedDate)}
            </button>
          )}
        </div>
        {personText && (
          <span className="text-sm" title={personText} aria-label="worth a person's check">
            👤
          </span>
        )}
        {!isAdvice && !isCancelled && <ActionsMenu items={items} disabled={busy} />}
      </div>
      {advice && (
        <div className="text-xs mt-0.5 opacity-80" data-testid="fav2-line">
          {advice}
          {whyText && (
            <button
              type="button"
              className="ml-1 underline"
              aria-expanded={showWhy}
              data-testid="fav2-why-toggle"
              onClick={() => setShowWhy(!showWhy)}
            >
              Why?
            </button>
          )}
        </div>
      )}
      {showWhy && whyText && (
        <div className="text-xs mt-1 p-2 rounded-sm bg-skin-primary whitespace-pre-line" data-testid="fav2-why">
          {whyText}
        </div>
      )}
      {error && <div className="text-xs mt-1 text-red">{error}</div>}
    </div>
  );
}

function Section({ title, rows, open, onToggle, renderRows, testId }) {
  return (
    // Only a section with rows grows — an open but EMPTY one (Assigned, until FAV books
    // crews) would otherwise take half the pane and squeeze the list that has rows.
    <div className={`${open && rows.length > 0 ? "flex-1" : ""} flex flex-col min-h-0 overflow-hidden`} data-testid={testId}>
      <button type="button" className="py-2 flex gap-2 items-center" onClick={onToggle}>
        <span className="font-bold flex-1 text-left">
          {title} ({rows.length})
        </span>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" className="fill-skin-primary">
          <path d={open ? CHEVRON_UP : CHEVRON_DOWN} />
        </svg>
      </button>
      <div className={`${open && rows.length > 0 ? "flex-1 overflow-auto" : "h-0 overflow-hidden"} transition-all duration-200`}>
        {open && renderRows(rows)}
      </div>
    </div>
  );
}

const FavInboxPanel = ({ scheduleArray, setDragObject, setDragObjectInvoiceNo, setDropObject }) => {
  const { rows, adviceRows, cancelledRows, loading, requestJump } = useContext(FavInboxContext);
  // Accordions, top to bottom: Assigned, Unassigned, [Unscheduled — TEMPORARY], Deferred
  // (Leland 2026-10-13). "Unscheduled" = FileMaker's advice-only (reserve:false) jobs:
  // nothing booked, FAV 2.0's proposal + why, a ghost on the grid. Remove with the ghosts.
  const [open, setOpen] = useState({ assigned: true, unassigned: true, unscheduled: true, cancelled: true, deferred: false });
  const { assigned, unassigned, deferred } = groupInbox(rows);

  const projectFor = (row) => (scheduleArray || []).find((j) => jobKey(j) === row.jobId) || null;

  // The same three calls a real drag + drop makes (UnassignedProjects / ScheduleScreen
  // onDragStarted + hasBeenDropped), so schedule.context runs its normal drop path.
  const onDrop = (project, dropId) => {
    if (!dropId) return;
    setDragObject(project.id);
    setDragObjectInvoiceNo(project.invoiceNo);
    setDropObject({ id: dropId });
  };

  // Advice-only rows have no booked date — jump to FAV 2.0's proposed start instead.
  const jumpTo = (row) =>
    requestJump(row.bookedDate ? row : { ...row, bookedDate: row.why && row.why.fav2 && row.why.fav2.startDateISO });
  const renderRows = (list) =>
    list.map((row) => <InboxRow key={row.id} row={row} project={projectFor(row)} onDrop={onDrop} onJump={jumpTo} />);

  if (loading) return null;

  const toggle = (key) => () => setOpen((o) => ({ ...o, [key]: !o[key] }));
  return (
    <div className="flex-1 flex flex-col min-h-0 pt-1" data-testid="fav-inbox">
      <Section title="Assigned" testId="fav-inbox-assigned" rows={assigned} open={open.assigned} onToggle={toggle("assigned")} renderRows={renderRows} />
      <Section title="Unassigned" testId="fav-inbox-unassigned" rows={unassigned} open={open.unassigned} onToggle={toggle("unassigned")} renderRows={renderRows} />
      <Section title="Unscheduled" testId="fav-inbox-unscheduled" rows={adviceRows || []} open={open.unscheduled} onToggle={toggle("unscheduled")} renderRows={renderRows} />
      <Section title="Customer cancelled" testId="fav-inbox-cancelled" rows={cancelledRows || []} open={open.cancelled} onToggle={toggle("cancelled")} renderRows={renderRows} />
      <Section title="Deferred" testId="fav-inbox-deferred" rows={deferred} open={open.deferred} onToggle={toggle("deferred")} renderRows={renderRows} />
    </div>
  );
};

export default FavInboxPanel;
